import React from 'react';
import PropTypes from 'prop-types';
import { Placeholder, RichText } from '@sitecore-jss/sitecore-jss-react';
import { connect } from 'react-redux';
import ReplaceableText from '../../../utils/text/ReplaceableText/index';

const fieldsPropType = PropTypes.shape({
  testid: PropTypes.shape({ value: PropTypes.string }),
  heading: PropTypes.shape({ value: PropTypes.string }),
  intro: PropTypes.shape({ value: PropTypes.string }),
  premiumChangeAlertHeading: PropTypes.shape({ value: PropTypes.string }),
  premiumChangeAlertContent: PropTypes.shape({ value: PropTypes.string }),
  footerImage: PropTypes.shape({
    value: PropTypes.shape({ src: PropTypes.string }),
  }),
  background: PropTypes.shape({
    value: PropTypes.shape({ src: PropTypes.string }),
  }),
});

const renderingPropType = PropTypes.object;

const PremiumChangeAlertBase = ({ show, fields }) => {
  if (!show) {
    return null;
  }
  const heading = fields?.premiumChangeAlertHeading?.value;
  const content = fields?.premiumChangeAlertContent?.value;

  if (!heading || !content) {
    return null;
  }

  return (
    <div
      className="vds-notification vds-notification--informational vds-margin-top-spacing-4"
      data-testid="premium-change-alert"
      role="alert"
    >
      <div className="vds-notification__inner">
        <div className="vds-notification__icon-container">
          <span className="vds-notification__icon-status"></span>
        </div>
        <div className="vds-notification__body">
          <div className="vds-notification__content">
            <div className="vds-notification__title-container">
              <h2 className="vds-notification__title">{heading}</h2>
            </div>
            <div className="vds-notification__message">
              <div className="vds-notification__message-main">
                <RichText field={{ value: content }} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

PremiumChangeAlertBase.propTypes = {
  show: PropTypes.bool,
  fields: fieldsPropType,
};

const PremiumChangeAlert = connect(state => ({
  show: !!state.appState?.quoteRevalidation?.premiumChangeAlert,
}))(PremiumChangeAlertBase);

const DefaultLayout = ({ rendering, fields }) => (
  <div className="vds-page-container__inner">
    <div className="vds-section-heading vds-section-heading--centred">
      <div className="vds-section-heading__inner">
        <ReplaceableText
          tag="h1"
          className="vds-section-heading__title"
          field={fields.heading}
          data-testid={`${fields.testid?.value}-heading`}
        />
        <RichText
          className="vds-section-heading__subtitle"
          field={fields.intro}
          data-testid={`${fields.testid?.value}-intro`}
        />
      </div>
    </div>
    <PremiumChangeAlert fields={fields} />

    <div className="vds-grid">
      <Placeholder name="quote-summary-hero" rendering={rendering} />
    </div>
  </div>
);

DefaultLayout.propTypes = {
  rendering: renderingPropType,
  fields: fieldsPropType,
};

const ReviewYourPlanLayout = ({ rendering, fields }) => (
  <div className="vds-quote-page-hero__inner">
    <div className="vds-quote-page-hero__main">
      <div className="vds-quote-page-hero__content">
        <ReplaceableText
          tag="h1"
          className="vds-quote-page-hero__heading"
          field={fields.heading}
          data-testid={`${fields.testid?.value}-heading`}
        />
        <RichText field={fields.intro} data-testid={`${fields.testid?.value}-intro`} />
      </div>
      <Placeholder name="quote-summary-hero" rendering={rendering} />
      <PremiumChangeAlert fields={fields} />
    </div>

    <div
      className="vds-quote-page-hero__image"
      style={{ backgroundImage: `url('${fields.footerImage?.value?.src}')` }}
    ></div>
  </div>
);

ReviewYourPlanLayout.propTypes = {
  rendering: renderingPropType,
  fields: fieldsPropType,
};

const QuoteSummaryHero = ({ rendering, fields, params }) => {
  const { variant, backgroundPosition } = params || {};

  let Content;
  switch (variant) {
    case 'your-quote':
      Content = DefaultLayout;
      break;
    case 'review-your-plan':
      Content = ReviewYourPlanLayout;
      break;
    default:
      Content = DefaultLayout;
  }

  return (
    <div className="vds-page-container__section vds-page-container__section--blue-bg">
      <div
        className="vds-quote-page-hero vds-quote-page-hero--animated vds-margin-bottom-spacing-0"
        style={{
          backgroundImage: `url('${fields.background?.value?.src}')`,
          backgroundSize: 'contain',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: backgroundPosition || 'bottom center',
        }}
      >
        <Content rendering={rendering} fields={fields} />
      </div>
    </div>
  );
};

QuoteSummaryHero.propTypes = {
  rendering: renderingPropType,
  fields: fieldsPropType,
  params: PropTypes.shape({
    variant: PropTypes.string,
    backgroundPosition: PropTypes.string,
  }),
};

export default QuoteSummaryHero;
